import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { authApi, formatApiError } from "../AuthContext";
import Modal, { ConfirmDialog } from "../Modal";

const empty = {
  name: "", event_type: "Festival", branch: "", date: "", time: "",
  location: "", age_category: "", requirements: "", capacity: 50,
  deadline: "", fee: "Ücretsiz", description: "", contact_person: "Vedat Coşkun",
  whatsapp: "905078664360", image_url: "",
};

export default function EventsSection() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => authApi.get("/admin/events").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const onDelete = async () => {
    await authApi.delete(`/admin/events/${confirmDel.id}`);
    toast.success("Etkinlik silindi");
    load();
  };

  return (
    <div data-testid="admin-events">
      <SectionHeader title="Etkinlikler" sub="Festival, kamp, seçme ve etkinliklerini buradan yönet." onAdd={() => setEditing({ ...empty })} addLabel="Yeni Etkinlik" />
      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((e) => (
          <div key={e.id} className="card-elev p-5" data-testid={`admin-event-${e.id}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="badge-status-soon">{e.event_type}</span>
              <span className="text-xs text-muted-foreground font-display uppercase tracking-wider">{e.branch}</span>
            </div>
            <div className="font-display text-xl font-bold uppercase tracking-tight">{e.name}</div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
              <div>📅 {e.date} · {e.time}</div>
              <div>📍 {e.location}</div>
              <div>👥 Yaş: {e.age_category}</div>
              <div>🎟 Kontenjan: {e.capacity}</div>
              <div className="sm:col-span-2">⏳ Son başvuru: {e.deadline}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setEditing(e)} className="btn-ghost text-xs px-2 py-1.5" data-testid={`event-edit-${e.id}`}>
                <Edit2 className="h-3 w-3" /> Düzenle
              </button>
              <button type="button" onClick={() => setConfirmDel(e)} className="btn-ghost text-xs px-2 py-1.5 text-destructive hover:text-destructive" data-testid={`event-delete-${e.id}`}>
                <Trash2 className="h-3 w-3" /> Sil
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && <EventForm item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={onDelete} title="Etkinliği sil?" body={confirmDel ? `${confirmDel.name} silinecek.` : ""} />
    </div>
  );
}

function EventForm({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, capacity: parseInt(form.capacity, 10) || 0 };
      if (isNew) await authApi.post("/admin/events", payload);
      else await authApi.put(`/admin/events/${form.id}`, payload);
      toast.success(isNew ? "Etkinlik eklendi" : "Etkinlik güncellendi");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? "Yeni Etkinlik" : "Etkinliği Düzenle"} size="lg">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="event-form">
        <Field label="Adı" required><input className="input-base" required value={form.name} onChange={update("name")} data-testid="ef-name" /></Field>
        <Field label="Türü" required>
          <select className="input-base" required value={form.event_type} onChange={update("event_type")} data-testid="ef-type">
            {["Festival", "Kamp", "Turnuva", "Seçme", "Seminer", "Atölye"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Branş" required><input className="input-base" required value={form.branch} onChange={update("branch")} data-testid="ef-branch" /></Field>
        <Field label="Lokasyon" required><input className="input-base" required value={form.location} onChange={update("location")} data-testid="ef-location" /></Field>
        <Field label="Tarih" required><input type="date" className="input-base" required value={form.date} onChange={update("date")} data-testid="ef-date" /></Field>
        <Field label="Saat" required><input type="time" className="input-base" required value={form.time} onChange={update("time")} data-testid="ef-time" /></Field>
        <Field label="Yaş Kategorisi" required><input className="input-base" required value={form.age_category} onChange={update("age_category")} placeholder="örn: 8-15" data-testid="ef-age" /></Field>
        <Field label="Kontenjan" required><input type="number" className="input-base" required value={form.capacity} onChange={update("capacity")} data-testid="ef-capacity" /></Field>
        <Field label="Son Başvuru" required><input type="date" className="input-base" required value={form.deadline} onChange={update("deadline")} data-testid="ef-deadline" /></Field>
        <Field label="Ücret"><input className="input-base" value={form.fee} onChange={update("fee")} data-testid="ef-fee" /></Field>
        <Field label="Sorumlu Kişi"><input className="input-base" value={form.contact_person} onChange={update("contact_person")} data-testid="ef-contact" /></Field>
        <Field label="WhatsApp (numara)"><input className="input-base" value={form.whatsapp} onChange={update("whatsapp")} placeholder="905078664360" data-testid="ef-whatsapp" /></Field>
        <div className="sm:col-span-2"><Field label="Görsel URL"><input className="input-base" value={form.image_url || ""} onChange={update("image_url")} data-testid="ef-image" /></Field></div>
        <div className="sm:col-span-2"><Field label="Katılım Şartları" required><textarea className="input-base min-h-[60px]" required value={form.requirements} onChange={update("requirements")} data-testid="ef-req" /></Field></div>
        <div className="sm:col-span-2"><Field label="Açıklama" required><textarea className="input-base min-h-[100px]" required value={form.description} onChange={update("description")} data-testid="ef-desc" /></Field></div>
        <div className="sm:col-span-2 flex justify-end gap-3 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="btn-secondary">İptal</button>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="ef-submit">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-input">{label}{required && <span className="text-primary"> *</span>}</span>
      {children}
    </label>
  );
}

function SectionHeader({ title, sub, onAdd, addLabel }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
      <div>
        <h1 className="heading-lg">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{sub}</p>
      </div>
      {onAdd && (
        <button type="button" onClick={onAdd} className="btn-primary" data-testid="section-add">
          <Plus className="h-4 w-4" /> {addLabel}
        </button>
      )}
    </div>
  );
}
